/**
 * Homepage State Utilities
 *
 * Determines which homepage variant to show based on patient data.
 *
 * 3D Model:
 * - Gender: male | female
 * - Goal: pregnancy | preserve | egg-donation | sperm-donation
 * - Stage: explore | before-treatment | in-care | time-to-wait | complete
 *          + donor stages: screening | matched | stimulation | retrieval | approved | donating
 */

import AsyncStorage from "@react-native-async-storage/async-storage";
import {
  getPatientGoal,
  type Patient,
  type TreatmentIntent,
} from "@louise/patients/patients.types";
import type {
  HomepageState3D,
  HomepageStateKey,
  HomepageGender,
  HomepageGoal,
  HomepageStage,
  LegacyHomepageState,
} from "./homepage.types";
import {
  INTENT_TO_GOAL,
  INTENT_TO_STARTING_STAGE,
  STAGE_LABELS,
} from "./homepage.types";
import { getStageFromFertilityIntent } from "./homeStage";
import { normalizeHomepageStageForGoal } from "./normalizeHomepageStageForGoal";

export { normalizeHomepageStageForGoal } from "./normalizeHomepageStageForGoal";

/**
 * @deprecated Use HomepageState3D from homepage.types.ts
 * Homepage state variants based on patient journey (legacy 2D model)
 */
export type HomepageState = LegacyHomepageState;

/**
 * Mapping of onboarding answers to homepage states
 */
type OnboardingMapping = {
  gender: "AFAB" | "AMAB";
  intent: TreatmentIntent;
  state: HomepageState;
};

const ONBOARDING_TO_HOMEPAGE_STATE: OnboardingMapping[] = [
  // Female (AFAB) mappings
  {
    gender: "AFAB",
    intent: "Exploring",
    state: "female-explore",
  },
  {
    gender: "AFAB",
    intent: "Preservation",
    state: "female-preserve",
  },
  {
    gender: "AFAB",
    intent: "Conception",
    state: "female-explore",
  },
  {
    gender: "AFAB",
    intent: "StartingTreatment",
    state: "female-in-care",
  },
  {
    gender: "AFAB",
    intent: "InTreatment",
    state: "female-in-care",
  },
  {
    gender: "AFAB",
    intent: "Donation",
    state: "female-explore", // Fallback to explore
  },

  // Male (AMAB) mappings
  {
    gender: "AMAB",
    intent: "Exploring",
    state: "male-explore",
  },
  {
    gender: "AMAB",
    intent: "Conception",
    state: "male-explore",
  },
  {
    gender: "AMAB",
    intent: "StartingTreatment",
    state: "male-in-care",
  },
  {
    gender: "AMAB",
    intent: "InTreatment",
    state: "male-in-care",
  },
  {
    gender: "AMAB",
    intent: "Preservation",
    state: "male-explore", // Special case: male preservation falls back to explore
  },
  {
    gender: "AMAB",
    intent: "Donation",
    state: "male-explore", // Fallback to explore
  },
];

/**
 * Determines which homepage variant to show based on patient data
 *
 * @param patient - Patient data from API
 * @returns Homepage state to render
 */
export function getHomepageState(patient: Patient | undefined): HomepageState {
  // Default to female-preserve (current homepage) if no patient data
  if (!patient) {
    return "female-preserve";
  }

  const gender = patient.gender;
  const treatments = patient.treatments;

  // If no gender specified, default to female-preserve
  if (!gender || (gender !== "AFAB" && gender !== "AMAB")) {
    return "female-preserve";
  }

  // Get most recent treatment intent
  const mostRecentTreatment =
    treatments && treatments.length > 0
      ? treatments[treatments.length - 1]
      : null;

  const intent = mostRecentTreatment?.intent ?? null;

  // Find matching state from mapping
  const mapping = ONBOARDING_TO_HOMEPAGE_STATE.find(
    (m) => m.gender === gender && m.intent === intent,
  );

  if (mapping) {
    return mapping.state;
  }

  // Fallback: if no intent but gender is known
  if (!intent) {
    return gender === "AFAB" ? "female-explore" : "male-explore";
  }

  // Final fallback: default to female-preserve (current homepage)
  return "female-preserve";
}

/**
 * Gets a human-readable label for the homepage state
 * @deprecated Use getHomepageState3DLabels for new code
 */
export function getHomepageStateLabel(state: HomepageState): string {
  const labels: Record<HomepageState, string> = {
    "female-explore": "Female Explore",
    "female-preserve": "Female Preserve",
    "female-in-care": "Female In Care",
    "male-explore": "Male Explore",
    "male-in-care": "Male In Care",
  };

  return labels[state];
}

// =============================================================================
// 3D Homepage State (New Implementation)
// =============================================================================

/**
 * Determines gender dimension from patient data
 */
function getGenderFromPatient(
  patient: Patient | undefined,
): HomepageGender | null {
  if (
    !patient?.gender ||
    (patient.gender !== "AFAB" && patient.gender !== "AMAB")
  ) {
    return null;
  }
  return patient.gender === "AFAB" ? "female" : "male";
}

function getHomepageGoalFromPatient(
  patient: Patient | undefined,
): HomepageGoal | null {
  const goal = patient ? getPatientGoal(patient) : null;
  if (!goal || goal === "exploring") return null;

  switch (goal) {
    case "egg-freezing":
    case "egg-preserve":
    case "sperm-freezing":
    case "sperm-preserve":
      return "preserve";
    case "embryo-freezing":
    case "embryo-preserve":
      return "embryo-preserve";
    case "pregnancy":
      return "pregnancy";
    case "egg-donation":
      return "egg-donation";
    case "sperm-donation":
      return "sperm-donation";
    case "adoption":
      return "adoption";
    case "surrogacy":
      return "surrogacy";
    default:
      return null;
  }
}

/**
 * Determines starting stage from treatment intent
 * All intents start at explore except InTreatment which starts at in-care
 */
function getStageFromIntent(intent: TreatmentIntent | null): HomepageStage {
  if (!intent) return "explore";
  return INTENT_TO_STARTING_STAGE[intent] ?? "explore";
}

/**
 * Returns the full 3D homepage state
 * Uses the canonical fertility-intent stage when available. The legacy
 * homepageStage remains a fallback while records migrate to that contract.
 */
/**
 * Dev-only: read URL query params to override homepage state.
 * Usage: ?force_gender=male&force_goal=pregnancy&force_stage=before-treatment
 */
function getDevOverrides(): Partial<HomepageState3D> {
  if (typeof window === "undefined") return {};
  try {
    const params = new URLSearchParams(window.location.search);
    const overrides: Partial<HomepageState3D> = {};
    const g = params.get("force_gender");
    if (g === "male" || g === "female") overrides.gender = g;
    const goal = params.get("force_goal");
    if (goal) overrides.goal = goal as HomepageGoal;
    const treatmentGoal = params.get("dev_treatmentGoal");
    if (treatmentGoal && INTENT_TO_GOAL[treatmentGoal]) {
      // Keep the documented persona fixture and the legacy 3D homepage state
      // on the same treatment path. Without this, an embryo-freezing fixture
      // can render the real patient's default IVF journey in its hero.
      overrides.goal = INTENT_TO_GOAL[treatmentGoal];
    }
    const stage = params.get("force_stage");
    if (stage) overrides.stage = stage as HomepageStage;
    return overrides;
  } catch {
    return {};
  }
}

export function getHomepageState3D(
  patient: Patient | undefined,
): HomepageState3D | null {
  const treatment =
    patient?.treatments && patient.treatments.length > 0
      ? patient.treatments[patient.treatments.length - 1]
      : null;

  const gender = getGenderFromPatient(patient);
  const overrides = getDevOverrides();
  const resolvedGender = overrides.gender ?? gender;
  if (!resolvedGender) return null;
  const goal = overrides.goal ?? getHomepageGoalFromPatient(patient);
  if (!goal) return null;

  const intent = treatment?.intent ?? null;

  // `fertilityIntent.stage` is the durable onboarding lifecycle state. Prefer
  // it over the legacy homepageStage so a natural-trying user does not get
  // silently routed back to the generic explore or IVF planning experience.
  const fertilityIntentStage = getStageFromFertilityIntent(
    patient?.fertilityIntent?.stage,
  );
  const persistedStage = patient?.homepageStage as
    HomepageStage | null | undefined;
  // Normalize legacy get-ready stage to before-treatment
  const rawStage =
    fertilityIntentStage ?? persistedStage ?? getStageFromIntent(intent);
  const stage = normalizeHomepageStageForGoal(
    goal,
    rawStage === "get-ready" ? "before-treatment" : rawStage,
  );

  return {
    gender: resolvedGender,
    goal,
    stage: overrides.stage ?? stage,
  };
}

/**
 * Serializes 3D state to a string key for variant routing
 */
export function getHomepageStateKey(state: HomepageState3D): HomepageStateKey {
  return `${state.gender}-${state.goal}-${state.stage}`;
}

/**
 * Maps new 3D state to legacy state names for backwards compatibility
 */
export function mapToLegacyState(state: HomepageState3D): LegacyHomepageState {
  const { gender, goal, stage } = state;

  if (gender === "female") {
    if (goal === "preserve") return "female-preserve";
    if (stage === "in-care" || stage === "time-to-wait") {
      return "female-in-care";
    }
    return "female-explore";
  } else {
    if (goal === "preserve") return "male-explore"; // Male preserve falls back to explore
    if (stage === "in-care" || stage === "time-to-wait") return "male-in-care";
    return "male-explore";
  }
}

/**
 * Gets human-readable labels for the 3D state
 */
const GOAL_LABELS: Record<HomepageGoal, string> = {
  pregnancy: "Pregnancy",
  preserve: "Preserve",
  "egg-donation": "Egg Donation",
  "sperm-donation": "Sperm Donation",
  adoption: "Adoption",
  "embryo-preserve": "Embryo Preserve",
  surrogacy: "Surrogacy",
};

export function getHomepageState3DLabels(state: HomepageState3D): {
  gender: string;
  goal: string;
  stage: string;
} {
  return {
    gender: state.gender === "female" ? "Female" : "Male",
    goal: GOAL_LABELS[state.goal] ?? state.goal,
    stage: STAGE_LABELS[state.stage],
  };
}

/**
 * Returns the onboarding path (primary step + optional sub-step) persisted
 * during the onboarding flow. Used by embryo-preserve variants to decide
 * between partner-specific and donor-specific content.
 */
const ONBOARDING_PATH_KEY = "@louise/onboarding_path";

export async function getOnboardingPath(): Promise<{
  primary?: string;
  subStep?: string;
} | null> {
  try {
    const raw = await AsyncStorage.getItem(ONBOARDING_PATH_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const record = parsed as Record<string, unknown>;
    return {
      primary: typeof record.primary === "string" ? record.primary : undefined,
      subStep: typeof record.subStep === "string" ? record.subStep : undefined,
    };
  } catch {
    return null;
  }
}
